<template>
  <div class="content">
    <card v-if="isCreateMode" class="text-left">
      <template slot="header">
        <h4 class="card-title">투표 생성</h4>
        <p class="card-category">타입·대상·스케줄은 생성 후에는 바꿀 수 없습니다. 그 외 항목은 아래 각 카드에서 바로 함께 입력합니다.</p>
      </template>

      <div class="row mb-2">
        <div class="col-md-4">
          <label class="col-form-label">타입</label>
          <el-select v-model="createForm.type" style="width: 100%">
            <el-option v-for="t in PollTypeOptions" :key="t.value" :label="t.label" :value="t.value" />
          </el-select>
        </div>
      </div>
      <div v-if="activePollType === PollType.FUNDING" class="row">
        <div class="col-md-12">
          <label class="col-form-label">아티스트 · 멤버</label>
          <ArtistSelect
            :show-title="false"
            :initial-unit-id="0"
            :include-unit-artist="true"
            :show-all-units="true"
            :show-members="true"
            :is-multiple="false"
            @unitChangeEvent="onCreateUnitChange"
            @memberChangeEvent="onCreateMemberChange"
          />
        </div>
      </div>
    </card>

    <card v-if="!isCreateMode" class="text-left detail-summary-card">
      <template slot="header">
        <div class="d-flex justify-content-between align-items-start flex-wrap">
          <div>
            <h4 class="card-title">
              투표 상세
              <span :class="sourceBadgeClass" class="badge ml-2">{{ sourceLabel }}</span>
            </h4>
            <p class="card-category mb-0">{{ poll.title }}</p>
          </div>
          <div class="summary-actions">
            <base-button v-if="publicVoteUrl" @click="openPublicVote" type="default" size="sm">유저 화면 열기</base-button>
            <base-button @click="goBack" type="default" size="sm">목록으로</base-button>
          </div>
        </div>
      </template>

      <div class="summary-grid">
        <div class="summary-item">
          <span class="summary-label">운영 상태</span>
          <span :class="operationBadgeClass" class="badge">{{ operationLabel }}</span>
        </div>
        <div class="summary-item">
          <span class="summary-label">노출</span>
          <span :class="visibilityBadgeClass" class="badge">{{ visibilityLabel }}</span>
        </div>
        <div v-if="activePollType === PollType.FUNDING" class="summary-item">
          <span class="summary-label">그룹</span>
          <strong>{{ poll.unitName || poll.artistHomeName || "-" }}</strong>
        </div>
        <div v-if="activePollType === PollType.FUNDING" class="summary-item">
          <span class="summary-label">대상</span>
          <strong>{{ targetLabel }}</strong>
        </div>
        <div class="summary-item summary-item-wide">
          <span class="summary-label">예정 공개일</span>
          <strong>{{ publishedAtLabel }}</strong>
        </div>
        <div class="summary-item summary-item-wide">
          <span class="summary-label">투표 기간</span>
          <strong>{{ period || "-" }}</strong>
        </div>
        <div v-if="activePollType === PollType.FUNDING" class="summary-item summary-item-wide">
          <span class="summary-label">연동 스케줄</span>
          <div>
            <strong>{{ scheduleLabel }}</strong>
            <base-button v-if="poll.scheduleId && poll.unitId" @click="openSchedule" type="default" size="sm" class="ml-2">스케줄 확인</base-button>
          </div>
        </div>
        <div v-if="activePollType === PollType.FUNDING" class="summary-item">
          <span class="summary-label">목표 젬</span>
          <strong>{{ targetGemLabel }}</strong>
        </div>
        <div v-if="activePollType === PollType.FUNDING" class="summary-item">
          <span class="summary-label">현재 누적</span>
          <strong>{{ currentGemLabel }}</strong>
        </div>
        <div v-if="activePollType === PollType.FUNDING" class="summary-item">
          <span class="summary-label">달성률</span>
          <strong>{{ fundingRateLabel }}</strong>
        </div>
      </div>
    </card>

    <card class="text-left visibility-control-card" :class="isShow ? 'visibility-on' : 'visibility-off'">
      <template slot="header">
        <div class="d-flex justify-content-between align-items-center flex-wrap">
          <div>
            <h4 class="card-title">노출 관리</h4>
            <p class="card-category mb-0">앱·웹 목록과 위젯에서 이 투표를 보여줄지 결정합니다. 상태와 일정은 변경하지 않습니다.</p>
          </div>
          <span :class="visibilityBadgeClass" class="badge visibility-state-badge">{{ visibilityLabel }}</span>
        </div>
      </template>
      <div class="visibility-control-row">
        <div>
          <div class="visibility-title">{{ isShow ? "현재 유저에게 노출 중" : "현재 유저에게 미노출" }}</div>
          <div class="visibility-desc">"설정 저장"을 눌러야 반영됩니다. 예정 공개일 이후 투표는 노출을 켜면 유저 화면에 나타날 수 있습니다.</div>
        </div>
        <div class="visibility-switch-wrap">
          <span class="visibility-switch-label">미노출</span>
          <span :style="visibilityEditable ? '' : 'pointer-events:none;opacity:0.5'">
            <base-switch :value="isShow" @input="onVisibilityToggle" type="primary" on-text="ON" off-text="OFF" />
          </span>
          <span class="visibility-switch-label">노출</span>
        </div>
      </div>
      <div v-if="!visibilityEditable" class="mt-2 text-muted small">닫힘·취소 상태에서는 다시 노출할 수 없습니다.</div>
    </card>

    <card class="text-left">
      <template slot="header">
        <h4 class="card-title">일정 설정</h4>
        <p class="card-category">
          예정 공개일은 유저 화면의 예정 노출 시점이고, 시작·종료 일시는 투표 진행 기간과 정산 기준입니다. 준비·예정 상태이고 참여가 없는 투표만 수정할 수
          있습니다.
        </p>
      </template>

      <div v-if="isCreateMode && activePollType === PollType.FUNDING" class="row">
        <div class="col-md-12">
          <label class="col-form-label">연동 스케줄</label>
          <div class="d-flex mb-1" style="gap: 6px">
            <el-input v-model="createForm.scheduleKeyword" placeholder="[축하] 스케줄명 검색" @keyup.enter.native="searchSchedules" />
            <base-button @click="searchSchedules" type="default" size="sm" :disabled="!createForm.unitId">검색</base-button>
          </div>
          <el-select v-model="createForm.scheduleId" placeholder="스케줄 선택" clearable filterable style="width: 100%" @change="onScheduleSelected">
            <el-option
              v-for="schedule in createForm.scheduleOptions"
              :key="schedule.scheduleId"
              :label="scheduleOptionLabel(schedule)"
              :value="schedule.scheduleId"
            />
          </el-select>
          <div v-if="createForm.selectedSchedule" class="schedule-preview">
            <div>
              <strong>#{{ createForm.selectedSchedule.scheduleId }}</strong> {{ createForm.selectedSchedule.title }}
            </div>
            <div class="text-muted">{{ formatSchedulePeriod(createForm.selectedSchedule) }}</div>
            <base-button @click="applyScheduleDefaults" type="default" size="sm">스케줄 기준 제목/일정 적용</base-button>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-4">
          <label class="col-form-label">예정 공개 일시</label>
          <el-date-picker v-model="periodForm.publishedAt" type="datetime" placeholder="예정 공개" style="width: 100%" :disabled="!periodEditable" />
        </div>
        <div class="col-md-4">
          <label class="col-form-label">투표 시작 일시</label>
          <el-date-picker v-model="periodForm.startAt" type="datetime" placeholder="시작" style="width: 100%" :disabled="!periodEditable" />
        </div>
        <div class="col-md-4">
          <label class="col-form-label">투표 종료 일시</label>
          <el-date-picker v-model="periodForm.endAt" type="datetime" placeholder="종료" style="width: 100%" :disabled="!periodEditable" />
        </div>
      </div>
      <div v-if="!isCreateMode" class="row align-items-center">
        <div class="col-sm-12 col-form-label text-muted">
          <span v-if="periodEditable">저장하면 예정 공개일과 투표 기간, 유저 화면 상태 기준이 함께 변경됩니다.</span>
          <span v-else>진행이 시작됐거나 참여가 있는 투표는 일정을 수정할 수 없습니다.</span>
        </div>
      </div>
    </card>

    <card class="text-left">
      <template slot="header">
        <h4 class="card-title">제목 편집</h4>
        <p class="card-category">제목은 상태와 무관하게 저장됩니다. 영어·일본어 미입력 시 앱은 한국어 제목으로 표시됩니다.</p>
      </template>

      <div class="row">
        <label class="col-sm-2 col-form-label">제목 (KO)</label>
        <div class="col-sm-10">
          <base-input v-model="titleForm.ko" placeholder="한국어 제목 (필수)" />
        </div>
        <label class="col-sm-2 col-form-label">제목 (EN)</label>
        <div class="col-sm-10">
          <base-input v-model="titleForm.en" placeholder="English title (선택 · 미입력 시 한국어로 표시)" />
        </div>
        <label class="col-sm-2 col-form-label">제목 (JA)</label>
        <div class="col-sm-10">
          <base-input v-model="titleForm.ja" placeholder="日本語タイトル (선택 · 미입력 시 한국어로 표시)" />
        </div>
      </div>
    </card>

    <ranking-settings-card
      v-if="activePollType === PollType.RANKING"
      :image-url="featuredImageUrl"
      :editable="editable"
      @upload="onFeaturedImageUpload"
      @remove="clearFeaturedImage"
    />

    <card class="text-left">
      <template slot="header">
        <h4 class="card-title">젬 경제 설정</h4>
        <p class="card-category">준비·예정 상태에서만 설정할 수 있습니다. 목표 젬과 1회 투표당 차감 젬(로열/블립)·사용 여부를 설정합니다.</p>
      </template>

      <div v-if="activePollType === PollType.FUNDING" class="row">
        <label class="col-sm-2 col-form-label">목표 젬</label>
        <div class="col-sm-4">
          <base-input type="number" placeholder="100000" v-model.number="settings.gemTarget" :disabled="!editable" />
          <span class="form-text text-muted">100% 달성 기준 젬 (로열 환산).</span>
        </div>
        <label class="col-sm-2 col-form-label">자동 계산 참여수</label>
        <div class="col-sm-4">
          <base-input :value="derivedTargetLabel" disabled />
          <span class="form-text text-muted">목표 젬과 로열 1회 수량으로 자동 계산되는 달성률 기준값입니다.</span>
        </div>
      </div>

      <div class="row">
        <label class="col-sm-2 col-form-label">로열 / 1회</label>
        <div class="col-sm-4">
          <base-input type="number" placeholder="1" v-model.number="settings.suffrageCount" :disabled="!editable" />
          <span class="form-text text-muted">1회 투표당 차감 로열 젬 (≥1, 환산 기준점). 정책 기본 1.</span>
        </div>
        <label class="col-sm-2 col-form-label">블립 / 1회</label>
        <div class="col-sm-4">
          <base-input type="number" placeholder="20" v-model.number="settings.freeSuffrageCount" :disabled="!editable" />
          <span class="form-text text-muted">1회 투표당 차감 블립 젬 (≥0). 정책 기본 20 (1로열=20블립 가치).</span>
        </div>
      </div>

      <div class="row">
        <label class="col-sm-2 col-form-label">로열 사용</label>
        <div class="col-sm-4">
          <span class="d-inline-block" :style="editable ? '' : 'pointer-events:none;opacity:0.5'">
            <base-switch v-model="settings.isSuffrage" type="primary" on-text="ON" off-text="OFF" />
          </span>
        </div>
        <label class="col-sm-2 col-form-label">블립 사용</label>
        <div class="col-sm-4">
          <span class="d-inline-block" :style="editable ? '' : 'pointer-events:none;opacity:0.5'">
            <base-switch v-model="settings.isFreeSuffrage" type="primary" on-text="ON" off-text="OFF" />
          </span>
        </div>
      </div>

      <div v-if="!isCreateMode" class="row">
        <div class="col-sm-12 col-form-label text-muted">현재 누적 젬: {{ currentGemLabel }} · 서포트 달성률: {{ fundingRateLabel }}</div>
      </div>
    </card>

    <card class="text-left">
      <template slot="header">
        <h4 class="card-title">참여 제한 설정</h4>
        <p class="card-category">
          1인/IP 일일 참여 횟수(0=무제한)와 기기 중복 체크. 서포트는 모금이라 기본 무제한(0). 어뷰즈가 의심될 때만 제한하세요. (운영 상태와 무관하게 즉시 적용)
        </p>
      </template>
      <div class="row">
        <label class="col-sm-3 col-form-label">1인 일일 횟수 (0=무제한)</label>
        <div class="col-sm-3"><base-input type="number" placeholder="0" v-model.number="limits.userCheckCount" /></div>
        <label class="col-sm-3 col-form-label">IP 일일 횟수 (0=무제한)</label>
        <div class="col-sm-3"><base-input type="number" placeholder="0" v-model.number="limits.ipCheckCount" /></div>
      </div>
      <div class="row">
        <label class="col-sm-3 col-form-label">전체기간 누적 횟수 (0=무제한)</label>
        <div class="col-sm-3"><base-input type="number" placeholder="0" v-model.number="limits.userTotalCheckCount" /></div>
      </div>
      <div class="row">
        <label class="col-sm-3 col-form-label">기기 중복 체크</label>
        <div class="col-sm-9"><base-switch v-model="limits.isDeviceCheck" type="primary" on-text="ON" off-text="OFF" /></div>
      </div>
    </card>

    <card class="text-left">
      <template slot="header">
        <h4 class="card-title">안내 · 가이드 본문</h4>
        <p class="card-category">
          앱 상세 화면에 노출되는 본문입니다. 로케일(ko·en·ja)별로 작성하며, 토큰 {@아티스트홈명}·{@팬덤명}·{@스케줄명}·{@펀딩종료일시}·{@펀딩종료일시+5} 는
          앱에서 자동 치환됩니다. 상태와 무관하게 저장됩니다.
          <b>미작성 시 투표 기본 템플릿이 상속</b>되어 노출됩니다.
        </p>
      </template>

      <div class="row">
        <div class="col-sm-12">
          <label class="col-form-label">안내</label>
          <div v-if="noticeInherited" class="alert alert-info py-2 px-3 mb-2">
            <b>서포트 기본 템플릿</b>을 불러왔습니다(상속 중). 그대로 두면 기본 템플릿이 노출되고, <b>수정·저장하면 이 서포트 전용</b>으로 override 됩니다.
          </div>
          <blip-i18n-editor v-model="notice" :tokens="true" :image="true" :upload-image="uploadImageFn" placeholder="안내 본문을 입력하세요" />
          <div class="mt-2 d-flex align-items-center">
            <base-button v-if="!isCreateMode && !noticeInherited" @click="resetBody('notice')" type="neutral" :disabled="resetting">
              기본 템플릿으로 되돌리기
            </base-button>
            <span class="ml-2 badge badge-pill" :class="noticeDirty ? 'badge-warning' : 'badge-success'">
              {{ noticeDirty ? "작업 중 · 미저장" : "저장됨" }}
            </span>
          </div>
        </div>
      </div>

      <hr />

      <div class="row">
        <div class="col-sm-12">
          <label class="col-form-label">가이드</label>
          <div v-if="guideInherited" class="alert alert-info py-2 px-3 mb-2">
            <b>서포트 기본 템플릿</b>을 불러왔습니다(상속 중). 그대로 두면 기본 템플릿이 노출되고, <b>수정·저장하면 이 서포트 전용</b>으로 override 됩니다.
          </div>
          <blip-i18n-editor v-model="guide" :tokens="true" :image="true" :upload-image="uploadImageFn" placeholder="가이드 본문을 입력하세요" />
          <div class="mt-2 d-flex align-items-center">
            <base-button v-if="!isCreateMode && !guideInherited" @click="resetBody('guide')" type="neutral" :disabled="resetting">
              기본 템플릿으로 되돌리기
            </base-button>
            <span class="ml-2 badge badge-pill" :class="guideDirty ? 'badge-warning' : 'badge-success'">
              {{ guideDirty ? "작업 중 · 미저장" : "저장됨" }}
            </span>
          </div>
        </div>
      </div>
    </card>

    <card class="text-left">
      <template slot="header">
        <h4 class="card-title">선택지 설정</h4>
        <p class="card-category">
          선택지별 이름(ko·en·ja)과 이미지를 설정합니다. 서포트는 보통 1개(서포트 대상), 경쟁 투표는 여러 개를 둡니다. 준비·예정 상태에서만 편집할 수 있으며,
          제거한 선택지는 소프트삭제(복구·집계 보존)됩니다. en·ja 미입력 시 ko 로 폴백됩니다.
        </p>
      </template>

      <draggable v-model="optionsForm" :options="{ handle: '.opt-drag-handle', disabled: !editable, animation: 150 }">
        <div v-for="(opt, idx) in optionsForm" :key="opt._k" class="row mb-3 pb-3 border-bottom align-items-start">
          <div class="col-sm-1 text-center d-flex align-items-center justify-content-center">
            <span class="opt-drag-handle" :style="{ cursor: editable ? 'grab' : 'not-allowed', fontSize: '1.4rem', color: '#bbb' }" title="드래그하여 순서 변경"
              >⠿</span
            >
          </div>
          <div class="col-sm-2 text-center">
            <img v-if="opt.imageUrl" :src="opt.imageUrl" alt="옵션 이미지" style="max-width: 100%; max-height: 90px; border-radius: 6px" />
            <div v-else class="text-muted small py-4" style="border: 1px dashed #ccc; border-radius: 6px">이미지 없음</div>
          </div>
          <div class="col-sm-6" :class="{ 'opt-name-dimmed': opt.unitId || opt.memberId }">
            <div class="row">
              <div class="col-sm-4">
                <base-input v-model="opt.ko" placeholder="이름 (한국어, 필수)" :disabled="!editable || !!(opt.unitId || opt.memberId)" />
              </div>
              <div class="col-sm-4"><base-input v-model="opt.en" placeholder="English (선택)" :disabled="!editable || !!(opt.unitId || opt.memberId)" /></div>
              <div class="col-sm-4"><base-input v-model="opt.ja" placeholder="日本語 (선택)" :disabled="!editable || !!(opt.unitId || opt.memberId)" /></div>
            </div>
            <div v-if="opt.unitId || opt.memberId" class="row mt-1">
              <div class="col-12 small text-muted">
                아티스트 추가로 등록된 선택지입니다 (이름 수정 불가)
                <span v-if="opt.unitId" class="ml-2">· 유닛ID {{ opt.unitId }}</span>
                <span v-if="opt.memberId" class="ml-2">· 멤버ID {{ opt.memberId }}</span>
              </div>
            </div>
            <div v-if="!isCreateMode" class="row mt-1">
              <div class="col-12 small text-muted">
                득표 <strong>{{ opt.voteCount.toLocaleString() }}</strong> ({{ opt.votePercentage }}%)
                <span class="ml-2">· 로열 {{ opt.voteCountSuffrage.toLocaleString() }}</span>
                <span class="ml-2">· 블립 {{ opt.voteCountFreeSuffrage.toLocaleString() }}</span>
              </div>
            </div>
          </div>
          <div class="col-sm-3 text-right">
            <label class="btn btn-sm btn-outline-primary mb-1" :class="{ disabled: !editable }">
              이미지 선택
              <input type="file" accept="image/*" style="display: none" :disabled="!editable" @change="onOptionImage($event, idx)" />
            </label>
            <base-button v-if="opt.imageUrl" @click="clearOptionImage(idx)" type="default" size="sm" :disabled="!editable">이미지 제거</base-button>
            <base-button @click="removeOptionRow(idx)" type="danger" size="sm" :disabled="!editable || optionsForm.length <= 1">선택지 삭제</base-button>
          </div>
        </div>
      </draggable>

      <div class="row">
        <div class="col-sm-12">
          <base-button @click="addOptionRow()" type="default" :disabled="!editable">선택지 추가</base-button>
          <base-button @click="openArtistAdd" type="default" :disabled="!editable">아티스트 추가</base-button>
        </div>
      </div>
      <div v-if="!editable" class="row">
        <div class="col-sm-12 col-form-label text-muted small">진행이 시작된 뒤에는 선택지를 편집할 수 없습니다.</div>
      </div>
    </card>

    <artist-group-add-modal
      :visible.sync="artistAddVisible"
      group-section-desc="체크한 그룹의 이름이 선택지로 추가됩니다."
      :preselected-group-unit-ids="preselectedGroupUnitIds"
      :resolve-preselected-member-ids="resolvePreselectedMemberIds"
      @apply="onArtistAddApply"
    />

    <card v-if="!isCreateMode" class="text-left">
      <div class="row">
        <div class="col-sm-8 col-form-label">
          <span v-if="closable" class="text-muted">
            닫으면 이 투표는 <strong>닫힘(CLOSED)</strong> 상태가 되고 즉시 미노출됩니다. 유저가 이미 상세 화면에 있어도 추가 투표는 차단됩니다. 환불은 발생하지
            않습니다.
          </span>
          <span v-else class="text-muted">이미 종료·닫힘·취소된 투표는 닫을 수 없습니다.</span>
        </div>
        <div class="col-sm-4 text-right">
          <base-button @click="closePoll" type="warning" :disabled="!closable">닫기 (미노출·투표 차단)</base-button>
        </div>
      </div>
    </card>

    <card v-if="!isCreateMode" class="text-left">
      <div class="row">
        <div class="col-sm-8 col-form-label">
          <span v-if="cancellable" class="text-muted">
            중도취소하면 참여자 전원에게 로열·블립 젬이 <strong>전액 환불</strong>되고, 투표는 취소·비노출 상태가 됩니다. 되돌릴 수 없습니다.
          </span>
          <span v-else class="text-muted">이미 종료·취소된 투표는 중도취소할 수 없습니다.</span>
        </div>
        <div class="col-sm-4 text-right">
          <base-button @click="cancelFunding" type="danger" :disabled="!cancellable">중도취소 (전액 환불)</base-button>
        </div>
      </div>
    </card>

    <card v-if="!isCreateMode" class="text-left">
      <div class="row">
        <div class="col-sm-8 col-form-label">
          <span v-if="deletable" class="text-muted"> 준비·예정 상태이고 참여가 없는 투표를 <strong>완전히 삭제</strong>합니다. 되돌릴 수 없습니다. </span>
          <span v-else class="text-muted">진행이 시작됐거나 참여가 있는 투표는 삭제할 수 없습니다 (중도취소를 사용하세요).</span>
        </div>
        <div class="col-sm-4 text-right">
          <base-button @click="deletePoll" type="danger" :disabled="!deletable">삭제</base-button>
        </div>
      </div>
    </card>

    <div class="row">
      <div class="col-sm-12 text-right">
        <base-button @click="goBack" type="default">목록으로</base-button>
        <base-button v-if="isCreateMode" @click="createPoll" type="primary">생성</base-button>
        <base-button v-else @click="saveVote" type="primary" :disabled="savingVote">{{ savingVote ? "저장 중…" : "설정 저장" }}</base-button>
      </div>
    </div>
  </div>
</template>
<script>
import axios from "axios";
import { BlipI18nEditor } from "@space-oddity-inc/editor-vue2";
import "@space-oddity-inc/editor-vue2/dist/style.css";
import { Card, BaseInput, BaseButton, BaseSwitch } from "@/components/index";
import { Alert } from "@/util/Alert";
import { formatDateTime } from "@/util/DateUtils";
import { BlipFileUploader, UploadCategory } from "@/util/BlipFileUploader";
import draggable from "vuedraggable";
import { PollType, PollTypeOptions } from "@/common/PollTypes";
import RankingSettingsCard from "@/pages/Poll/RankingSettingsCard.vue";
import ArtistSelect from "@/pages/Components/ArtistSelect.vue";
import ArtistGroupAddModal from "@/pages/Components/ArtistGroupAddModal.vue";

const LANGS = ["ko", "en", "ja"];

// 빈 본문(빈 <p> 등) 은 서버 검증(html 비어있으면 안 됨)을 통과하지 못하므로 저장 대상에서 제외한다.
function isBlankHtml(html) {
  if (!html) return true;
  const text = html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();
  if (text.length > 0) return false;
  return !/<img\b/i.test(html);
}

// 에디터가 넘긴 File 의 자연 크기 (uploader requestUploadUrl 의 width/height 용).
function readImageSize(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({ width: 0, height: 0 });
    };
    img.src = url;
  });
}

// 저장될 본문(로케일 맵)이 실제 참조하는 업로드 이미지의 fileId 집합을 추출한다.
// 임베드 형태는 `${imageServerUrl}${fileId}`(+선택적 ?쿼리) — 외부 URL은 prefix 불일치로 자연히 제외된다.
function collectUploadedFileIds(htmlMap) {
  const prefix = window.Endpoints.imageServerUrl;
  const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(escaped + "([0-9a-zA-Z]+)", "g");
  const ids = new Set();
  Object.values(htmlMap || {}).forEach((html) => {
    if (!html) return;
    let m;
    while ((m = re.exec(html)) !== null) ids.add(m[1]);
  });
  return ids;
}

const EDITABLE_STATUSES = ["CREATED", "PUBLISHED"];
const STATUS_LABELS = {
  CREATED: "준비",
  PUBLISHED: "예정",
  WORKING: "진행중",
  FINISHED: "종료",
  CLOSED: "닫힘",
  CANCELLED: "취소",
};

export default {
  components: {
    Card,
    BaseInput,
    BaseButton,
    BaseSwitch,
    BlipI18nEditor,
    draggable,
    RankingSettingsCard,
    ArtistSelect,
    ArtistGroupAddModal,
  },
  async created() {
    const pollId = this.$route.query.pollId;
    if (!pollId) {
      this.isCreateMode = true;
      return;
    }
    this.pollId = pollId;
    await this.getData();
    await this.getBody();
  },
  data() {
    return {
      PollType,
      PollTypeOptions,
      isCreateMode: false,
      createForm: this.emptyCreateForm(),
      pollId: null,
      poll: {},
      featuredImageFileId: null,
      featuredImageUrl: null,
      titleForm: { ko: "", en: "", ja: "" },
      isShow: false,
      periodForm: { publishedAt: null, startAt: null, endAt: null },
      settings: {
        gemTarget: null,
        suffrageCount: null,
        freeSuffrageCount: null,
        isSuffrage: true,
        isFreeSuffrage: true,
      },
      limits: { userCheckCount: 0, ipCheckCount: 0, userTotalCheckCount: 0, isDeviceCheck: false },
      savingVote: false,
      notice: {},
      guide: {},
      // 마지막 저장/로드 시점의 본문 스냅샷(JSON). 현재 값과 비교해 미저장 여부 판단.
      savedNotice: "{}",
      savedGuide: "{}",
      // 기본 템플릿으로 되돌리기(per-poll override DELETE) 진행 플래그.
      resetting: false,
      // 서버 effective 응답의 상속 플래그 — true=기본 템플릿 상속(편집기엔 프리필됨), false=이 서포트 전용 override.
      noticeInherited: false,
      guideInherited: false,
      optionsForm: [],
      optKeySeq: 0,
      artistAddVisible: false,
      preselectedGroupUnitIds: [],
    };
  },
  computed: {
    activePollType() {
      return this.isCreateMode ? this.createForm.type : this.poll.type;
    },
    editable() {
      return this.isCreateMode || EDITABLE_STATUSES.includes(this.poll.status);
    },
    periodEditable() {
      return this.editable && Number(this.poll.voteCount || 0) === 0;
    },
    statusLabel() {
      if (this.poll.status === "FINISHED" && this.poll.fundingResult === "SUCCESS") return "종료(성공)";
      if (this.poll.status === "FINISHED" && this.poll.fundingResult === "FAIL") return "종료(실패)";
      return STATUS_LABELS[this.poll.status] || this.poll.status || "";
    },
    // 랭킹 투표는 목표 젬이 없어 voteCountTarget 로만 판단하면 항상 "설정 필요"가 된다 —
    // FUNDING 은 목표 젬, 그 외 타입은 선택지 존재 여부로 판단한다 (목록 페이지 needsSetup 과 동일한 기준).
    needsSetup() {
      if (!EDITABLE_STATUSES.includes(this.poll.status)) return false;
      if (this.activePollType === PollType.FUNDING) return Number(this.poll.voteCountTarget || 0) <= 0;
      return (this.optionsForm || []).length <= 0;
    },
    operationLabel() {
      if (this.needsSetup) return "설정 필요";
      if (this.isPublishReserved) return "공개 예약";
      return this.statusLabel;
    },
    operationBadgeClass() {
      if (this.needsSetup) return "badge-warning";
      if (this.isPublishReserved) return "badge-info";
      if (this.poll.status === "WORKING") return "badge-success";
      if (this.poll.status === "PUBLISHED") return "badge-info";
      if (this.poll.status === "CANCELLED" || this.poll.status === "CLOSED" || this.poll.status === "FINISHED") return "badge-secondary";
      return "badge-light";
    },
    visibilityLabel() {
      return this.isShow ? "노출" : "미노출";
    },
    visibilityBadgeClass() {
      return this.isShow ? "badge-success" : "badge-secondary";
    },
    visibilityEditable() {
      return !["CLOSED", "CANCELLED"].includes(this.poll.status);
    },
    sourceLabel() {
      if (!this.poll.resourceId) return "불러오는 중";
      return this.poll.isAutoCreated ? "자동 생성" : "수동 생성";
    },
    sourceBadgeClass() {
      return this.poll.isAutoCreated ? "badge-info" : "badge-secondary";
    },
    scheduleLabel() {
      if (!this.poll.scheduleId) return "미연동";
      return this.poll.scheduleName ? `#${this.poll.scheduleId} · ${this.poll.scheduleName}` : `#${this.poll.scheduleId}`;
    },
    targetLabel() {
      return this.poll.memberName ? `멤버 · ${this.poll.memberName}` : "그룹 전체";
    },
    period() {
      if (!this.poll.startAt) return "";
      return `${formatDateTime(this.poll.startAt)} ~ ${formatDateTime(this.poll.finishAt)}`;
    },
    publishedAtLabel() {
      return this.poll.publishedAt ? formatDateTime(this.poll.publishedAt) : "-";
    },
    isPublishReserved() {
      return this.poll.status === "CREATED" && this.poll.publishedAt && new Date(this.poll.publishedAt).getTime() > Date.now();
    },
    targetGemLabel() {
      if (this.poll.gemTarget == null) return "미설정";
      return `${Number(this.poll.gemTarget || 0).toLocaleString()} 젬`;
    },
    currentGemLabel() {
      return `${Number(this.poll.currentGem || 0).toLocaleString()} 젬`;
    },
    fundingRateLabel() {
      if (this.poll.fundingRatePercent == null) return "—";
      return `${Number(this.poll.fundingRatePercent).toLocaleString()}%`;
    },
    derivedTargetLabel() {
      const gem = this.settings.gemTarget;
      const royal = this.settings.suffrageCount;
      if (!Number.isInteger(gem) || gem <= 0 || !Number.isInteger(royal) || royal < 1) return "—";
      return `${Math.ceil(gem / royal).toLocaleString()} 회`;
    },
    publicVoteUrl() {
      const id = this.poll.resourceId || this.pollId;
      if (!id) return "";
      return `${window.Endpoints.homepageUrl}/votes/${id}`;
    },
    // 준비·예정(CREATED/PUBLISHED) + 참여 0건만 삭제 가능 — 서버가 cast 0 최종 판정(돈/원장 보호).
    deletable() {
      return EDITABLE_STATUSES.includes(this.poll.status) && Number(this.poll.voteCount || 0) === 0;
    },
    // 종료(정산)·취소된 투표는 환불 claim 이 이미 소비돼 중도취소 불가 (blip-server cancelFunding 멱등 no-op).
    cancellable() {
      return ["CREATED", "PUBLISHED", "WORKING"].includes(this.poll.status);
    },
    closable() {
      return ["CREATED", "PUBLISHED", "WORKING"].includes(this.poll.status);
    },
    // 현재 본문이 마지막 저장/로드 스냅샷과 다르면 미저장(작업 중).
    noticeDirty() {
      return JSON.stringify(this.notice) !== this.savedNotice;
    },
    guideDirty() {
      return JSON.stringify(this.guide) !== this.savedGuide;
    },
    // noticeInherited/guideInherited 는 서버 effective 응답 플래그(data) — 편집기엔 기본 템플릿이 프리필되므로 blank 판정 불가.
    // 에디터(자식)가 호출할 때 this 바인딩이 유지되도록 화살표로 감싸 prop 으로 전달.
    uploadImageFn() {
      return (file) => this.uploadImage(file);
    },
  },
  methods: {
    emptyCreateForm() {
      return {
        type: PollType.FUNDING,
        unitId: null,
        memberId: null,
        memberName: "",
        isUnitArtist: false,
        scheduleId: null,
        scheduleKeyword: "",
        scheduleOptions: [],
        selectedSchedule: null,
      };
    },
    onCreateUnitChange(param) {
      this.createForm.unitId = param && param.unitId ? param.unitId : null;
      this.createForm.memberId = null;
      this.createForm.memberName = "";
      this.createForm.isUnitArtist = false;
      this.createForm.scheduleId = null;
      this.createForm.selectedSchedule = null;
      this.createForm.scheduleOptions = [];
    },
    onCreateMemberChange(param) {
      if (!param || !param.unitId) return;
      this.createForm.unitId = param.unitId;
      this.createForm.memberId = param.memberId && !param.isUnitArtist ? param.memberId : null;
      this.createForm.memberName = param.memberName || "";
      this.createForm.isUnitArtist = !!param.isUnitArtist;
      this.createForm.scheduleId = null;
      this.createForm.selectedSchedule = null;
    },
    async searchSchedules() {
      if (!this.createForm.unitId) {
        window.Toast.error("먼저 아티스트를 선택해주세요");
        return;
      }
      const now = new Date();
      const from = new Date(now.getTime());
      from.setMonth(from.getMonth() - 6);
      const to = new Date(now.getTime());
      to.setMonth(to.getMonth() + 6);
      const params = {
        unitId: this.createForm.unitId,
        startTime: from.toISOString(),
        endTime: to.toISOString(),
        typeId: 4,
      };
      if (this.createForm.memberId) params.memberId = this.createForm.memberId;
      if (this.createForm.scheduleKeyword && this.createForm.scheduleKeyword.trim()) params.keyword = this.createForm.scheduleKeyword.trim();
      try {
        const res = await this.$axios.get("/schedule", { params });
        this.createForm.scheduleOptions = res.data.data || [];
        if (this.createForm.scheduleOptions.length === 0) window.Toast.error("검색된 [축하] 스케줄이 없습니다");
      } catch (err) {
        window.Toast.error(err.response?.data?.error?.message ?? "스케줄 검색 중 에러가 발생하였습니다");
      }
    },
    onScheduleSelected(scheduleId) {
      this.createForm.selectedSchedule = (this.createForm.scheduleOptions || []).find((s) => s.scheduleId === scheduleId) || null;
    },
    scheduleOptionLabel(schedule) {
      const target = schedule.members && schedule.members.length > 0 ? ` / 멤버 ${schedule.members.map((m) => m.name).join(", ")}` : " / 그룹";
      return `#${schedule.scheduleId} ${schedule.title}${target} / ${formatDateTime(schedule.startTime)}`;
    },
    formatSchedulePeriod(schedule) {
      return `${formatDateTime(schedule.startTime)} ~ ${formatDateTime(schedule.endTime)}`;
    },
    fundingTimeFromSchedule(schedule, daysBefore) {
      const source = new Date(schedule.startTime);
      const kst = new Date(source.getTime() + 9 * 60 * 60 * 1000);
      return new Date(Date.UTC(kst.getUTCFullYear(), kst.getUTCMonth(), kst.getUTCDate() - daysBefore, 7, 0, 0, 0));
    },
    applyScheduleDefaults() {
      if (!this.createForm.selectedSchedule) return;
      const schedule = this.createForm.selectedSchedule;
      this.periodForm.publishedAt = this.fundingTimeFromSchedule(schedule, 60);
      this.periodForm.startAt = this.fundingTimeFromSchedule(schedule, 53);
      this.periodForm.endAt = this.fundingTimeFromSchedule(schedule, 23);
      if (!this.titleForm.ko || !this.titleForm.ko.trim()) {
        this.titleForm.ko = `${schedule.title} 서포트 이벤트!`;
      }
    },
    // 기본 항목(제목·일정·대상)만 먼저 생성하고, 나머지(젬 경제·참여 제한·안내·가이드·선택지)는
    // 수정 페이지와 동일한 카드·저장 메서드를 그대로 재사용해 이어서 저장한다.
    // title/period/settings/limits/body/options/visibility 를 한 번에 검증한다.
    // 생성(POST /votes)과 수정(PUT /votes/{pollId})이 동일한 payload 모양을 쓰므로 공용으로 뺐다.
    validateVoteForm() {
      if (!this.titleForm.ko || !this.titleForm.ko.trim()) {
        window.Toast.error("한국어 제목은 필수입니다");
        return false;
      }
      if (this.isCreateMode && this.createForm.type === PollType.FUNDING && !this.createForm.unitId) {
        window.Toast.error("아티스트를 선택해주세요");
        return false;
      }
      if (!this.periodForm.publishedAt || !this.periodForm.startAt || !this.periodForm.endAt) {
        window.Toast.error("예정 공개·시작·종료 일시를 입력해주세요");
        return false;
      }
      if (new Date(this.periodForm.publishedAt).getTime() > new Date(this.periodForm.startAt).getTime()) {
        window.Toast.error("예정 공개 일시는 시작보다 빠르거나 같아야 합니다");
        return false;
      }
      if (new Date(this.periodForm.startAt).getTime() >= new Date(this.periodForm.endAt).getTime()) {
        window.Toast.error("시작은 종료보다 빨라야 합니다");
        return false;
      }
      const { gemTarget, suffrageCount, freeSuffrageCount, isSuffrage, isFreeSuffrage } = this.settings;
      const isFunding = this.activePollType === PollType.FUNDING;
      if (isFunding && (!Number.isInteger(gemTarget) || gemTarget <= 0)) {
        window.Toast.error("목표 젬은 1 이상의 정수여야 합니다");
        return false;
      }
      if (!Number.isInteger(suffrageCount) || suffrageCount < 1) {
        window.Toast.error("로열 수량은 1 이상의 정수여야 합니다 (환산 기준점)");
        return false;
      }
      if (!Number.isInteger(freeSuffrageCount) || freeSuffrageCount < 0) {
        window.Toast.error("블립 수량은 0 이상의 정수여야 합니다");
        return false;
      }
      if (!isSuffrage && !isFreeSuffrage) {
        window.Toast.error("로열·블립 중 최소 하나는 사용해야 합니다");
        return false;
      }
      const u = Number(this.limits.userCheckCount);
      const ip = Number(this.limits.ipCheckCount);
      const total = Number(this.limits.userTotalCheckCount);
      if (!Number.isInteger(u) || u < 0 || !Number.isInteger(ip) || ip < 0 || !Number.isInteger(total) || total < 0) {
        window.Toast.error("횟수는 0 이상의 정수여야 합니다 (0=무제한)");
        return false;
      }
      const rows = this.optionsForm || [];
      if (rows.length === 0) {
        window.Toast.error("선택지는 1개 이상이어야 합니다");
        return false;
      }
      if (rows.some((r) => !(r.ko || "").trim())) {
        window.Toast.error("모든 선택지의 한국어 이름은 필수입니다");
        return false;
      }
      return true;
    },
    buildVoteParams() {
      const titleI18n = {};
      if (this.titleForm.en && this.titleForm.en.trim()) titleI18n.en = this.titleForm.en.trim();
      if (this.titleForm.ja && this.titleForm.ja.trim()) titleI18n.ja = this.titleForm.ja.trim();

      const isFunding = this.activePollType === PollType.FUNDING;
      const { gemTarget, suffrageCount, freeSuffrageCount, isSuffrage, isFreeSuffrage } = this.settings;

      // ko=canonical name, en/ja 는 입력된 것만(빈 값은 ko 폴백 보존). sortOrder=표시 순서.
      const options = (this.optionsForm || []).map((r, idx) => {
        const opt = { name: r.ko.trim(), sortOrder: idx };
        const nameI18n = {};
        if ((r.en || "").trim()) nameI18n.en = r.en.trim();
        if ((r.ja || "").trim()) nameI18n.ja = r.ja.trim();
        if (Object.keys(nameI18n).length) opt.nameI18n = nameI18n;
        if (r.imageFileId) opt.imageFileId = r.imageFileId;
        if (r.unitId) opt.unitId = r.unitId;
        if (r.memberId) opt.memberId = r.memberId;
        return opt;
      });

      const params = {
        type: this.activePollType,
        title: this.titleForm.ko.trim(),
        titleI18n,
        publishedAt: new Date(this.periodForm.publishedAt).toISOString(),
        startAt: new Date(this.periodForm.startAt).toISOString(),
        endAt: new Date(this.periodForm.endAt).toISOString(),
        isShow: this.isShow,
        suffrageCount,
        freeSuffrageCount,
        isSuffrage,
        isFreeSuffrage,
        userCheckCount: Number(this.limits.userCheckCount),
        ipCheckCount: Number(this.limits.ipCheckCount),
        userTotalCheckCount: Number(this.limits.userTotalCheckCount),
        isDeviceCheck: !!this.limits.isDeviceCheck,
        options,
      };
      if (isFunding) params.gemTarget = gemTarget;
      if (this.activePollType === PollType.RANKING && this.featuredImageFileId) params.featuredImageFileId = this.featuredImageFileId;

      const noticeLangs = LANGS.filter((lang) => !isBlankHtml(this.notice[lang]));
      if (noticeLangs.length) {
        params.notice = {};
        noticeLangs.forEach((lang) => (params.notice[lang] = this.notice[lang]));
      }
      const guideLangs = LANGS.filter((lang) => !isBlankHtml(this.guide[lang]));
      if (guideLangs.length) {
        params.guide = {};
        guideLangs.forEach((lang) => (params.guide[lang] = this.guide[lang]));
      }

      // 대상(아티스트/멤버)·연동 스케줄은 생성 시점에만 정할 수 있다 — 랭킹 투표는 이 UI 자체가 없다.
      if (this.isCreateMode && this.activePollType === PollType.FUNDING) {
        params.unitId = this.createForm.unitId;
        if (this.createForm.memberId) params.memberId = this.createForm.memberId;
        if (this.createForm.scheduleId) params.scheduleId = this.createForm.scheduleId;
      }

      return params;
    },
    async markUploadedImages() {
      const ids = (this.optionsForm || []).map((r) => r.imageFileId).filter(Boolean);
      if (this.featuredImageFileId) ids.push(this.featuredImageFileId);
      if (ids.length) await this.markImagesUploaded(ids);
      await this.confirmReferencedImages({ ...this.notice, ...this.guide });
    },
    async createPoll() {
      if (!this.validateVoteForm()) return;
      const params = this.buildVoteParams();
      try {
        const res = await this.$axios.post("/votes", { params });
        const newId = res.data.data && res.data.data.resourceId;
        if (!newId) {
          window.Toast.error("생성에 실패했습니다");
          return;
        }
        this.pollId = newId;
        this.isCreateMode = false;
        this.$router.replace(`/poll/detail?pollId=${newId}`);
        await this.markUploadedImages();
        window.Toast.success("생성되었습니다");
        await this.getData();
        await this.getBody();
      } catch (err) {
        window.Toast.error(err.response?.data?.error?.message ?? "생성 중 에러가 발생하였습니다");
      }
    },
    async saveVote() {
      if (!this.validateVoteForm()) return;
      const params = this.buildVoteParams();
      this.savingVote = true;
      try {
        await this.$axios.put(`/votes/${this.pollId}`, { params });
        await this.markUploadedImages();
        window.Toast.success("설정이 저장되었습니다");
        await this.getData();
        await this.getBody();
      } catch (err) {
        window.Toast.error(err.response?.data?.error?.message ?? "저장 중 에러가 발생하였습니다");
      } finally {
        this.savingVote = false;
      }
    },
    async getData() {
      return this.$axios.get(`/votes/${this.pollId}`).then((res) => {
        const data = res.data.data;
        this.poll = data;
        this.titleForm = {
          ko: data.title || "",
          en: data.titleI18n?.en || "",
          ja: data.titleI18n?.ja || "",
        };
        this.isShow = !!data.isShow;
        this.periodForm = {
          publishedAt: data.publishedAt ? new Date(data.publishedAt) : null,
          startAt: data.startAt ? new Date(data.startAt) : null,
          endAt: data.finishAt ? new Date(data.finishAt) : null,
        };
        this.settings = {
          gemTarget: data.gemTarget == null ? null : Number(data.gemTarget),
          suffrageCount: data.suffrageCount == null ? null : Number(data.suffrageCount),
          freeSuffrageCount: data.freeSuffrageCount == null ? null : Number(data.freeSuffrageCount),
          isSuffrage: !!data.isSuffrage,
          isFreeSuffrage: !!data.isFreeSuffrage,
        };
        this.limits = {
          userCheckCount: data.userCheckCount == null ? 0 : Number(data.userCheckCount),
          ipCheckCount: data.ipCheckCount == null ? 0 : Number(data.ipCheckCount),
          userTotalCheckCount: data.userTotalCheckCount == null ? 0 : Number(data.userTotalCheckCount),
          isDeviceCheck: !!data.isDeviceCheck,
        };
        this.featuredImageFileId = data.featuredImageFileId || null;
        this.featuredImageUrl = data.featuredImageFileId ? window.Endpoints.imageServerUrl + data.featuredImageFileId : null;
        // 선택지: admin 응답의 raw(name=ko, nameI18n, imageFileId) → 편집 행. imageUrl 은 미리보기용 파생.
        this.optionsForm = (data.options || []).map((o) => ({
          ko: o.name || "",
          en: o.nameI18n?.en || "",
          ja: o.nameI18n?.ja || "",
          imageFileId: o.imageFileId || null,
          imageUrl: o.imageFileId ? window.Endpoints.imageServerUrl + o.imageFileId : null,
          unitId: o.unitId || null,
          memberId: o.memberId || null,
          // 득표현황(read-only, denorm) — admin 응답의 vote-stat 필드
          voteCount: Number(o.voteCount || 0),
          votePercentage: Number(o.votePercentage || 0),
          voteCountSuffrage: Number(o.voteCountSuffrage || 0),
          voteCountFreeSuffrage: Number(o.voteCountFreeSuffrage || 0),
          _k: this.nextOptKey(),
        }));
      });
    },
    // isShow 는 더 이상 개별 저장을 갖지 않는다 — 로컬 상태만 바꾸고, 실제 반영은 "설정 저장"(saveVote) 이 한다.
    onVisibilityToggle(next) {
      if (!this.visibilityEditable) return;
      this.isShow = next;
    },
    async closePoll() {
      if (!this.closable) return;
      if (
        await Alert.dangerousConfirmCancel(
          "투표 닫기",
          `이 투표를 <strong>닫힘(CLOSED)</strong> 상태로 전환하고 즉시 미노출합니다.<br>유저가 이미 상세 화면에 있어도 추가 투표는 차단됩니다.<br><br><strong>환불은 발생하지 않습니다.</strong> 환불이 필요하면 중도취소를 사용하세요.<br>-제목: ${this.poll.title}<br>계속하시겠습니까?`,
          "닫기",
          "취소"
        )
      ) {
        try {
          await this.$axios.put(`/votes/${this.pollId}/close`);
          window.Toast.success("투표가 닫힘 상태로 전환되었습니다");
          await this.getData();
        } catch (err) {
          window.Toast.error(err.response?.data?.error?.message ?? "투표 닫기 중 에러가 발생하였습니다");
        }
      }
    },
    openSchedule() {
      if (!this.poll.scheduleId || !this.poll.unitId) return;
      window.open(`/schedules/scheduleForm?scheduleId=${this.poll.scheduleId}&unitId=${this.poll.unitId}`);
    },
    openPublicVote() {
      if (!this.publicVoteUrl) return;
      window.open(this.publicVoteUrl, "_blank");
    },
    async cancelFunding() {
      if (!this.cancellable) return;
      if (
        await Alert.dangerousConfirmCancel(
          "투표 중도취소",
          `중도취소하면 참여자 전원에게 로열·블립 젬이 <strong>전액 환불</strong>되고, 투표는 취소(비노출) 상태가 되며 <strong>되돌릴 수 없습니다</strong>.<br>-제목: ${
            this.poll.title
          }<br>-현재 누적 젬: ${Number(this.poll.currentGem || 0).toLocaleString()}<br>계속하시겠습니까?`,
          "중도취소",
          "닫기"
        )
      ) {
        try {
          const res = await this.$axios.put(`/votes/${this.pollId}/cancel`);
          const data = res.data.data || {};
          if (data.cancelled) {
            window.Toast.success(
              `중도취소 완료 — 환불 ${Number(data.userCount || 0).toLocaleString()}명 · 로열 ${Number(data.royalTotal || 0).toLocaleString()} · 블립 ${Number(
                data.blipTotal || 0
              ).toLocaleString()}`
            );
          } else {
            window.Toast.error("이미 정산되었거나 취소된 투표입니다");
          }
          await this.getData();
        } catch (err) {
          window.Toast.error(err.response?.data?.error?.message ?? "중도취소 중 에러가 발생하였습니다");
        }
      }
    },
    async deletePoll() {
      if (!this.deletable) return;
      if (
        await Alert.dangerousConfirmCancel(
          "투표 삭제",
          `이 투표를 <strong>완전히 삭제</strong>합니다. 준비·예정 상태이고 참여 0건일 때만 가능하며 되돌릴 수 없습니다.<br>-제목: ${this.poll.title}<br>계속하시겠습니까?`,
          "삭제",
          "닫기"
        )
      ) {
        try {
          await this.$axios.delete(`/votes/${this.pollId}`);
          window.Toast.success("삭제되었습니다");
          this.$router.push("/poll");
        } catch (err) {
          window.Toast.error(err.response?.data?.error?.message ?? "삭제 중 에러가 발생하였습니다");
        }
      }
    },
    // field 미지정 시 둘 다 로드(최초 진입). field 지정 시 해당 본문만 재동기화 —
    // 저장 직후 반대편(미저장) 작성분을 서버 값으로 덮어써 유실시키지 않도록.
    async getBody(field) {
      try {
        // effective = per-poll override 가 있으면 그것, 없으면 기본 템플릿 프리필(RAW 토큰형) + {field}Inherited 플래그.
        // → 미작성 투표도 기본 템플릿을 편집기에 불러와 "그걸 기반으로 이 투표만 약간 수정" 가능(수정·저장 시 per-poll override).
        const res = await this.$axios.get(`/votes/${this.pollId}/body/effective`);
        const data = res.data.data || {};
        if (!field || field === "notice") {
          this.notice = data.notice || {};
          this.savedNotice = JSON.stringify(this.notice);
          this.noticeInherited = !!data.noticeInherited;
        }
        if (!field || field === "guide") {
          this.guide = data.guide || {};
          this.savedGuide = JSON.stringify(this.guide);
          this.guideInherited = !!data.guideInherited;
        }
      } catch (err) {
        // 본문 미작성 등은 치명적이지 않음 — 빈 상태 유지
      }
    },
    // per-poll override 제거 → 기본 템플릿 상속으로 되돌림(DELETE). field 단위. 멱등.
    async resetBody(field) {
      const label = field === "notice" ? "안내" : "가이드";
      const ok = await Alert.dangerousConfirmCancel(
        "기본 템플릿으로 되돌리기",
        `${label} 본문의 이 서포트 전용 override 를 제거하고 기본 템플릿으로 되돌릴까요?`,
        "되돌리기",
        "취소"
      );
      if (!ok) return;
      this.resetting = true;
      try {
        await this.$axios.delete(`/votes/${this.pollId}/body`, { params: { field } });
        window.Toast.success(`${label} 본문을 기본 템플릿으로 되돌렸습니다`);
        await this.getBody(field);
      } catch (err) {
        window.Toast.error(err.response?.data?.error?.message ?? "되돌리기 중 에러가 발생하였습니다");
      } finally {
        this.resetting = false;
      }
    },
    // 저장 직후 호출. 본문이 참조하는 fileId 만 확정(state=1) → 미참조/미저장 업로드는 state=0(고아) 유지.
    // 확정 실패는 치명적이지 않다(image-man 은 state 무관하게 노출, 다음 저장 시 재확정) → 저장 성공을 막지 않게 삼킨다.
    async confirmReferencedImages(htmlMap) {
      const ids = collectUploadedFileIds(htmlMap);
      if (ids.size === 0) return;
      const uploader = new BlipFileUploader();
      for (const fileId of ids) {
        try {
          await uploader.markFileAsUploaded(fileId);
        } catch (e) {
          console.warn("[funding] 이미지 확정(state=1) 실패", fileId, e);
        }
      }
    },
    // 선택지 이미지(fileId 배열) 확정 — confirmReferencedImages 의 plain-id 버전. 실패는 삼킨다(저장은 이미 성공).
    async markImagesUploaded(ids) {
      const uploader = new BlipFileUploader();
      for (const fileId of ids) {
        try {
          await uploader.markFileAsUploaded(fileId);
        } catch (e) {
          console.warn("[funding] 선택지 이미지 확정(state=1) 실패", fileId, e);
        }
      }
    },
    async uploadImage(file) {
      const uploader = new BlipFileUploader();
      const { width, height } = await readImageSize(file);
      const result = await uploader.requestUploadUrl({
        category: UploadCategory.DASHBOARD,
        mimeType: file.type,
        fileName: file.name,
        width,
        height,
        fileSize: file.size,
      });
      const form = new FormData();
      Object.entries(result.fields).forEach(([key, value]) => form.append(key, value));
      form.append("file", file);
      // S3 presigned POST — 앱 axios($axios) 가 아닌 bare axios 로 보내 쿠키·기본 헤더를 싣지 않는다.
      await axios.post(result.uploadPath, form, { withCredentials: false });
      // GC-ready: 업로드 즉시 확정(state=1)하지 않는다. 본문 저장 시 실제 참조된 fileId만
      // markFileAsUploaded 로 확정 → 삽입 후 지우거나 저장하지 않은 이미지는 state=0(고아)로 남아
      // 향후 sweeper 가 안전히 수거할 수 있다. (width/height 는 requestUploadUrl 단계에서 이미 저장됨.)
      return window.Endpoints.imageServerUrl + result.fileId;
    },
    nextOptKey() {
      this.optKeySeq += 1;
      return this.optKeySeq;
    },
    addOptionRow(name = "", unitId = null, memberId = null, names = null) {
      this.optionsForm.push({
        ko: names?.ko || name,
        en: names?.en || "",
        ja: names?.ja || "",
        imageFileId: null,
        imageUrl: null,
        unitId,
        memberId,
        voteCount: 0,
        votePercentage: 0,
        voteCountSuffrage: 0,
        voteCountFreeSuffrage: 0,
        _k: this.nextOptKey(),
      });
    },
    openArtistAdd() {
      this.artistAddVisible = true;
      // 이미 그룹 자체로 추가된 선택지(unitId만 있고 memberId 없음)는 모달에 체크된 상태로 보여준다.
      this.preselectedGroupUnitIds = this.optionsForm.filter((o) => o.unitId && !o.memberId).map((o) => o.unitId);
    },
    resolvePreselectedMemberIds(unitId) {
      // 이 그룹에서 이미 멤버로 추가된 선택지는 모달에 체크된 상태로 보여준다.
      return this.optionsForm.filter((o) => o.unitId === unitId && o.memberId).map((o) => o.memberId);
    },
    // 아티스트 API 응답의 blipNames([{code, name}, ...])에서 언어별 이름을 뽑아낸다. 없으면 blipName(한국어)만 사용.
    extractArtistNames(item) {
      const blipNames = item.blipNames || [];
      const byCode = (code) => blipNames.find((n) => n.code === code)?.name || "";
      return {
        ko: byCode("ko") || item.blipName || "",
        en: byCode("en"),
        ja: byCode("ja"),
      };
    },
    onArtistAddApply({ groupUnits, memberUnits, memberGroupId }) {
      // 이미 선택지로 존재하는 항목(체크 표시로만 보여준 것)은 다시 추가하지 않고, 새로 체크한 항목만 추가한다.
      groupUnits.forEach((group) => {
        const alreadyAdded = this.optionsForm.some((o) => o.unitId === group.unitId && !o.memberId);
        if (!alreadyAdded) this.addOptionRow(group.blipName || group.name, group.unitId, null, this.extractArtistNames(group));
      });
      memberUnits.forEach((member) => {
        const alreadyAdded = this.optionsForm.some((o) => o.unitId === memberGroupId && o.memberId === member.artistId);
        if (!alreadyAdded) this.addOptionRow(member.blipName || member.name, memberGroupId, member.artistId, this.extractArtistNames(member));
      });
    },
    removeOptionRow(idx) {
      // 화면에서만 제거 — 저장 시 서버가 빠진 옵션을 IS_DELETED=1 소프트삭제(복구·집계 보존).
      this.optionsForm.splice(idx, 1);
    },
    async onOptionImage(event, idx) {
      const file = event.target.files && event.target.files[0];
      event.target.value = ""; // 같은 파일 재선택 허용
      if (!file) return;
      try {
        const url = await this.uploadImage(file);
        const prefix = window.Endpoints.imageServerUrl;
        const row = this.optionsForm[idx];
        row.imageUrl = url;
        row.imageFileId = url.startsWith(prefix) ? url.slice(prefix.length) : url;
      } catch (err) {
        window.Toast.error(err.response?.data?.error?.message ?? "이미지 업로드 중 에러가 발생하였습니다");
      }
    },
    clearOptionImage(idx) {
      const row = this.optionsForm[idx];
      row.imageFileId = null;
      row.imageUrl = null;
    },
    async onFeaturedImageUpload(file) {
      try {
        const url = await this.uploadImage(file);
        const prefix = window.Endpoints.imageServerUrl;
        this.featuredImageUrl = url;
        this.featuredImageFileId = url.startsWith(prefix) ? url.slice(prefix.length) : url;
      } catch (err) {
        window.Toast.error(err.response?.data?.error?.message ?? "이미지 업로드 중 에러가 발생하였습니다");
      }
    },
    clearFeaturedImage() {
      this.featuredImageFileId = null;
      this.featuredImageUrl = null;
    },
    goBack() {
      this.$router.push("/poll");
    },
  },
};
</script>
<style scoped>
/* ArtistSelect(공통 컴포넌트)의 아티스트(v-select) 아웃라인이 기본 검정이라
   이 페이지의 다른 el-select/el-input 아웃라인(#DCDFE6, element-ui 기본값)과 맞춘다.
   공통 컴포넌트 자체는 건드리지 않고 이 페이지 안에서만 덮어쓴다. */
::v-deep .vs__dropdown-toggle {
  border-color: #dcdfe6;
}
/* 멤버 el-select는 select-primary 클래스로 인해 _plugin-select.scss 의 테마 mixin이
   적용되어 border-color 가 lighten($black,5%) 로 다르게 나온다 — 같은 이유로 덮어쓴다. */
::v-deep .select-primary.el-select .el-input__inner {
  border-color: #dcdfe6 !important;
}
::v-deep .select-primary.el-select .el-input__inner:hover {
  border-color: #c0c4cc !important;
}
.schedule-preview {
  border: 1px solid #e9ecef;
  border-radius: 4px;
  margin-top: 8px;
  padding: 10px;
}
.detail-summary-card .card-title,
.visibility-control-card .card-title {
  margin-bottom: 4px;
}
.summary-actions {
  display: flex;
  gap: 8px;
  margin-top: 2px;
}
.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}
.summary-item {
  min-height: 68px;
  padding: 12px;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  background: #fff;
}
.summary-item-wide {
  grid-column: span 2;
}
.summary-label {
  display: block;
  margin-bottom: 6px;
  color: #6c757d;
  font-size: 12px;
  font-weight: 600;
}
.visibility-control-card {
  border-left: 4px solid #adb5bd;
}
.visibility-control-card.visibility-on {
  border-left-color: #00bf9a;
}
.visibility-control-card.visibility-off {
  border-left-color: #6c757d;
}
.visibility-state-badge {
  font-size: 13px;
  padding: 6px 10px;
}
.visibility-control-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.visibility-title {
  margin-bottom: 4px;
  font-size: 16px;
  font-weight: 700;
}
.visibility-desc {
  color: #6c757d;
  font-size: 13px;
}
.visibility-switch-wrap {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  background: #f8f9fa;
  white-space: nowrap;
}
.visibility-switch-label {
  color: #495057;
  font-size: 13px;
  font-weight: 600;
}
@media (max-width: 991px) {
  .summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .visibility-control-row {
    align-items: flex-start;
    flex-direction: column;
  }
}
@media (max-width: 575px) {
  .summary-grid {
    grid-template-columns: 1fr;
  }
  .summary-item-wide {
    grid-column: span 1;
  }
  .summary-actions {
    margin-top: 10px;
    width: 100%;
  }
}
</style>
